import React, {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {HiOutlinePencil} from 'react-icons/hi';
import {trackEvent} from '../../utils/tracking';
import {
  NormalizedDataset,
  downloadDatasetAsCSV,
  DATASET_STAGE_TYPE,
  datasetStatusToStage,
  ProjectWithDatasets,
} from '../../models/dataset';
import {isInternal} from '../../models/auth';
import {Modal} from '../../core/layout/modal';
import ReactTooltip from 'react-tooltip';
import {DatasetRequestMetadataConfirmedModal} from './dataset-request-metadata';
import {
  HiEllipsisVertical,
  HiOutlineDocumentArrowDown,
  HiOutlineArrowRightOnRectangle,
} from 'react-icons/hi2';
import {useAxios} from 'src/utils/http';
import {toast} from 'react-toastify';
import {ToastMessage} from 'src/core/components/toast';
import {useAuth} from 'src/hooks/auth';
import {moveDatasetToProject} from 'src/models/project';

export const ProjectDatasetCard = ({
  dataset,
  action,
  isLocked = false,
  projectID,
  isGhosted,
  allProjects = [],
  onDatasetMoved,
}: {
  dataset: NormalizedDataset;
  action?: (actionType: 'edit' | 'delete' | 'archive' | 'request') => void;
  isLocked?: boolean;
  projectID?: string;
  isGhosted?: boolean;
  allProjects?: ProjectWithDatasets[];
  onDatasetMoved?: () => void;
}) => {
  const http = useAxios();
  const {authState} = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [currentModal, currentModalChange] = useState<string>();
  const [showActionDropdown, setShowActionDropdown] = useState(false);

  const datasetCreated = dataset.createdAt
    ? new Date(dataset.createdAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : 'N/A';

  const [requestConfirmedModalOpen, requestConfirmedModalOpenChange] =
    useState(false);
  const [selectedTargetProjectId, setSelectedTargetProjectId] = useState('');
  const [isMoving, setIsMoving] = useState(false);

  // Filter out current project and closed projects for move targets
  const moveTargetProjects = allProjects.filter(
    p => p.project_id !== projectID && p.project_state === 'open'
  );
  const canMove = moveTargetProjects.length > 0;

  // Check if CSV download should be available (same logic as metadata-page)
  const isInternalUser = isInternal(authState.profile?.email || '');
  const datasetOrdered = [
    DATASET_STAGE_TYPE.DatasetOrdered,
    DATASET_STAGE_TYPE.DatasetDelivered,
  ].includes(datasetStatusToStage(dataset.status));
  const canDownloadCSV = isInternalUser || datasetOrdered;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowActionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [dataset]);

  const handleMoveDataset = () => {
    if (!selectedTargetProjectId || !projectID) return;
    setIsMoving(true);
    moveDatasetToProject(http, projectID, dataset.id, selectedTargetProjectId)
      .then(() => {
        toast(
          <ToastMessage title="Dataset moved successfully" icon="success" />
        );
        currentModalChange(undefined);
        setSelectedTargetProjectId('');
        onDatasetMoved?.();
      })
      .catch(err => {
        const errMsg = err?.response?.data?.message || 'Failed to move dataset';
        toast(<ToastMessage title={errMsg} icon="error" />);
      })
      .finally(() => {
        setIsMoving(false);
      });
  };

  const renderMoveDatasetModal = () => {
    const targetProject = moveTargetProjects.find(
      p => p.project_id === selectedTargetProjectId
    );
    return (
      <Modal
        isOpen={currentModal === 'moveDataset'}
        onRequestClose={() => {
          currentModalChange(undefined);
          setSelectedTargetProjectId('');
        }}
        className="w-96 max-w-lg"
      >
        <div className="text-lg mb-3">Move Dataset</div>
        <div className="text-sm text-gray-500 mb-3">
          Select a project to move <strong>{dataset.name}</strong> to:
        </div>
        <div className="mb-4 max-h-60 overflow-y-auto">
          {moveTargetProjects.map(project => (
            <button
              key={project.project_id}
              onClick={() => setSelectedTargetProjectId(project.project_id)}
              className={`w-full text-left px-3 py-2 rounded mb-1 text-sm ${
                selectedTargetProjectId === project.project_id
                  ? 'bg-blue-100 border border-blue-400'
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              {project.name}
            </button>
          ))}
        </div>
        {selectedTargetProjectId && (
          <div className="text-sm text-gray-600 mb-3">
            Move <strong>{dataset.name}</strong> to{' '}
            <strong>{targetProject?.name}</strong>?
          </div>
        )}
        <div className="flex flex-row-reverse space-x-2 space-x-reverse">
          <button
            className="btn btn-primary"
            onClick={handleMoveDataset}
            disabled={!selectedTargetProjectId || isMoving}
          >
            {isMoving ? 'Moving...' : 'Move'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
              currentModalChange(undefined);
              setSelectedTargetProjectId('');
            }}
          >
            Cancel
          </button>
        </div>
      </Modal>
    );
  };

  return (
    <>
      <div
        className={`grid grid-cols-[2fr_1fr_1.5fr_1.5fr_1.5fr_1fr_1fr_0.5fr] items-center px-4 py-2 border-b ${
          !isGhosted ? 'hover:bg-gray-50' : ''
        } relative text-sm ${isGhosted ? 'bg-gray-100' : ''}`}
      >
        {!isGhosted ? (
          <Link
            className="text-blue-600 hover:underline truncate"
            to={`/datasets/${dataset.id}`}
            onClick={() => {
              trackEvent('CLICK_VIEW_DATASET_DETAILS_BTN', {
                datasetId: dataset.id,
              });
            }}
          >
            {dataset.name}
          </Link>
        ) : (
          <div className="text-gray-700 opacity-50 cursor-not-allowed truncate">
            {dataset.name}
          </div>
        )}

        <div className="truncate">{dataset.id}</div>
        <div className="truncate">
          {dataset.userName
            ? dataset.userName
            : `${authState.profile?.firstname} ${authState.profile?.lastname}`}
        </div>
        <div>
          <span className="text-xs bg-gray-200 px-2 py-1 rounded">
            {dataset.status}
          </span>
        </div>
        <div className="truncate">{datasetCreated}</div>
        <div>{(dataset.studyCount ?? 0).toLocaleString('en-US')}</div>
        <div>{'0'}</div>

        {/* Ellipsis Button */}
        <div className="relative flex justify-end" ref={dropdownRef}>
          <button
            onClick={() => setShowActionDropdown(prev => !prev)}
            className={`p-2 hover:bg-gray-100 rounded-full ${
              isGhosted || isLocked ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="More actions"
            disabled={isGhosted || isLocked}
          >
            <HiEllipsisVertical className="w-6 h-6 text-gray-600" />
          </button>

          {showActionDropdown && (
            <div className="absolute right-0 top-full mt-2 w-44 bg-white border border-gray-200 rounded-md shadow-lg z-10">
              <ul className="text-sm text-gray-700">
                <li>
                  <button
                    onClick={() => {
                      trackEvent('CLICK_EDIT_DATASET_BTN', {
                        datasetId: dataset.id,
                      });
                      setShowActionDropdown(false);
                      action && action('edit');
                    }}
                    className="flex items-center px-4 py-2 w-full hover:bg-gray-50"
                  >
                    <HiOutlinePencil className="w-5 h-5 inline" />{' '}
                    <span className="ml-2">Rename</span>
                  </button>
                </li>
                {canMove && (
                  <li>
                    <button
                      onClick={() => {
                        trackEvent('CLICK_MOVE_DATASET_BTN', {
                          datasetId: dataset.id,
                          project_id: projectID,
                        });
                        setShowActionDropdown(false);
                        currentModalChange('moveDataset');
                      }}
                      className="flex items-center px-4 py-2 w-full hover:bg-gray-50"
                    >
                      <HiOutlineArrowRightOnRectangle className="w-5 h-5 inline" />{' '}
                      <span className="ml-2">Move</span>
                    </button>
                  </li>
                )}
                {canDownloadCSV && (
                  <li>
                    <button
                      onClick={async () => {
                        trackEvent('CLICK_DOWNLOAD_DATASET_AS_CSV_BTN', {
                          datasetId: dataset.id,
                          project_id: projectID,
                        });
                        setShowActionDropdown(false);
                        try {
                          await downloadDatasetAsCSV(http, dataset.id);
                        } catch (error) {
                          toast(
                            <ToastMessage
                              title="Failed to download CSV"
                              icon="error"
                            />
                          );
                        }
                      }}
                      className="flex items-center px-4 py-2 w-full hover:bg-gray-50"
                    >
                      <HiOutlineDocumentArrowDown className="w-5 h-5 inline" />
                      <span className="ml-2">Download CSV</span>
                    </button>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {renderMoveDatasetModal()}
      <DatasetRequestMetadataConfirmedModal
        isOpen={requestConfirmedModalOpen}
        onRequestClose={() => requestConfirmedModalOpenChange(false)}
      />
    </>
  );
};
